import { CREDIT_TRANSACTION_REASONS } from "@/types/domain";

export type PushNotificationType =
  | "request_sent"
  | "request_accepted"
  | "request_rejected"
  | "request_cancelled"
  | "new_matching_commute"
  | "system_announcement"
  | "credits_purchased"
  | "credits_granted"
  | "credits_refunded";

type NotificationBase = {
  type: PushNotificationType;
  entity_type?: "request" | "commute" | string;
  entity_id?: string;
  url?: string;
  actor_origin?: string;
  actor_destination?: string;
  actor_name?: string;
};

type RequestPayload = NotificationBase & {
  type:
    | "request_sent"
    | "request_accepted"
    | "request_rejected"
    | "request_cancelled";
  actor_name?: string;
  actor_role?: "driver" | "rider" | string;
};

type MatchingCommutePayload = NotificationBase & {
  type: "new_matching_commute";
  actor_name?: string;
};

type CreditsPayload = NotificationBase & {
  type: "credits_purchased" | "credits_granted" | "credits_refunded";
  amount?: number;
  reason?: string;
};

type SystemAnnouncementPayload = NotificationBase & {
  type: "system_announcement";
  message?: string;
};

export type NotificationPayload =
  | RequestPayload
  | MatchingCommutePayload
  | CreditsPayload
  | SystemAnnouncementPayload;

export type BuiltNotification = {
  title: string;
  body: string;
  url: string;
};

function asString(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (value == null) return undefined;
  return String(value);
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function asRole(value: unknown): "driver" | "rider" | undefined {
  const role = asString(value);
  if (role === "driver" || role === "rider") return role;
  return undefined;
}

function parseRouteFields(data: Record<string, unknown>) {
  return {
    actor_origin: asString(data.actor_origin),
    actor_destination: asString(data.actor_destination),
  };
}

export function parseNotificationPayload(
  input: unknown,
): NotificationPayload | null {
  if (!input || typeof input !== "object") return null;
  const data = input as Record<string, unknown>;
  const type = asString(data.type) as PushNotificationType | undefined;
  if (!type) return null;

  const base = {
    type,
    entity_type: asString(data.entity_type),
    entity_id: asString(data.entity_id),
    url: asString(data.url),
  };

  switch (type) {
    case "request_sent":
    case "request_accepted":
    case "request_rejected":
    case "request_cancelled":
      return {
        ...base,
        type,
        actor_name: asString(data.actor_name),
        actor_role: asRole(data.actor_role),
        ...parseRouteFields(data),
      };
    case "new_matching_commute":
      return {
        ...base,
        type,
        actor_name: asString(data.actor_name),
        ...parseRouteFields(data),
      };
    case "credits_purchased":
    case "credits_granted":
    case "credits_refunded":
      return {
        ...base,
        type,
        amount: asNumber(data.amount) ?? asNumber(data.credits) ?? 0,
        reason: asString(data.reason),
      };
    case "system_announcement":
      return {
        ...base,
        type,
        message: asString(data.message),
      };
    default:
      return null;
  }
}

export function buildNotification(payload: NotificationPayload): {
  title: string;
  body: string;
} {
  const { type } = payload;
  const origin = payload.actor_origin ?? "Origin";
  const destination = payload.actor_destination ?? "Destination";
  const actorName = payload.actor_name ?? "Someone";

  switch (type) {
    case "request_sent": {
      if (payload.actor_role === "driver") {
        return {
          title: "New Connection 🚗",
          body: `${actorName} is driving from ${origin} → ${destination}.`,
        };
      }

      return {
        title: "New Connection 👋",
        body: `${actorName} wants to join you from ${origin} → ${destination}.`,
      };
    }

    case "request_accepted":
      return {
        title: "You're Connected! 🎉",
        body: `${actorName} accepted your request. Coordinate your commute.`,
      };

    case "request_rejected":
      return {
        title: "Connection Declined 🙅",
        body: `${actorName} declined your request.`,
      };

    case "request_cancelled":
      return {
        title: "Connection Cancelled ↩️",
        body: `${actorName} cancelled your request.`,
      };

    case "new_matching_commute":
      return {
        title: `${actorName} Is Going Your Way ✨`,
        body: `A new commute route matches your route from ${origin} → ${destination}.`,
      };

    case "credits_granted":
    case "credits_purchased": {
      const amount = payload.amount ?? 0;
      switch (payload.reason) {
        case CREDIT_TRANSACTION_REASONS.SIGNUP_BONUS:
          return {
            title: "Welcome Bonus! 🎉",
            body: `You've been granted ${amount} free credits to get started.`,
          };
        case CREDIT_TRANSACTION_REASONS.BUNDLE_PURCHASE_STARTER:
        case CREDIT_TRANSACTION_REASONS.BUNDLE_PURCHASE_STANDARD:
        case CREDIT_TRANSACTION_REASONS.BUNDLE_PURCHASE_PRO:
          return {
            title: "Credits Purchased 💰",
            body: `Your account has been credited with ${amount} credits successfully.`,
          };
        case CREDIT_TRANSACTION_REASONS.MANUAL_ADJUSTMENT:
          return {
            title: "Balance Adjusted ⚖️",
            body: `Your credit balance was adjusted by ${amount} credits.`,
          };
        default:
          if (type === "credits_granted") {
            return {
              title: "Credits Granted 🎁",
              body: `You have received ${amount} credits!`,
            };
          }
          return {
            title: "Credits Added 💳",
            body: `Your account has been credited with ${amount} credits.`,
          };
      }
    }

    case "credits_refunded": {
      const amount = payload.amount ?? 0;
      return {
        title: "Payment Refunded 💸",
        body: `${amount} credits were removed after your payment refund was processed.`,
      };
    }

    case "system_announcement":
      return {
        title: "Announcement 📢",
        body: payload.message ?? "There is a new update.",
      };

    default:
      return {
        title: "Komut",
        body: "You have a new notification.",
      };
  }
}

export function buildNotificationUrl(payload: NotificationPayload): string {
  if (payload.url && payload.url.startsWith("/")) return payload.url;
  if (payload.entity_type === "request" && payload.entity_id) {
    return `/requests/${payload.entity_id}`;
  }
  if (payload.entity_type === "commute" && payload.entity_id) {
    return `/commutes/${payload.entity_id}`;
  }
  return "/notifications";
}

export function buildNotificationFromUnknown(
  input: unknown,
): BuiltNotification {
  const source =
    input &&
    typeof input === "object" &&
    "data" in (input as Record<string, unknown>)
      ? (input as Record<string, unknown>).data
      : input;

  const payload = parseNotificationPayload(source);
  if (!payload) {
    return {
      title: "Komut",
      body: "You have a new notification.",
      url: "/notifications",
    };
  }

  const { title, body } = buildNotification(payload);
  const url = buildNotificationUrl(payload);
  return { title, body, url };
}
